import { useCallback, useEffect, useMemo } from 'react';
import { useNavigation, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { useResponsive } from '@garnish/core';

import {
  type HeaderButtonProps,
  type HeaderIconItems,
  useHeaderButtonOptions,
  useHeaderLeft,
  useHeaderRight,
} from '../../../../../../components';
import { handleHardwareBackButtonPress } from '../../../../../../navigation';
import { joinOrSignScreenHeaderButtonsMessages as messages } from './useJoinOrSignScreenHeaderButtons.messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook that renders custom navigation header controls and conditionally
 * toggles various navigation options to improve user experience during loading.
 */
export const useJoinOrSignScreenHeaderButtons = (
  params: UseJoinOrSignScreenHeaderButtonsParams,
) => {
  const { isLoading } = params;

  const navigation = useNavigation();
  const router = useRouter();

  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Helpers ──────────────────────────────────────────────────────────

  const goBack = useCallback(() => {
    if (router.canDismiss()) {
      router.dismissAll();
      return;
    }

    router.navigate('/');
  }, [router]);

  // ─── Header Controls ─────────────────────────────────────────────────

  const headerLeftItems = useMemo<HeaderIconItems>(() => {
    // NOTE: On iOS, we render the "Join or Sign In" screen as a modal,
    //       therefore, rendering back button is not needed.
    if (Platform.OS === 'ios') return [];

    return [
      {
        key: 'join-or-sign-in-screen-header-back-btn',
        icon: 'IconArrowLeft',
        accessibilityLabel: formatMessage(messages.backBtnLabel),
        onPress: goBack,
      },
    ];
  }, [formatMessage, goBack]);

  const headerRightClose = useMemo<HeaderButtonProps>(
    () => ({
      testID: 'join-or-sign-in-screen-header-close-btn',
      accessibilityLabel: formatMessage(messages.closeBtnLabel),
      onPress: goBack,
    }),
    [formatMessage, goBack],
  );

  // ─────────────────────────────────────────────────────────────────────

  const shouldHideControls = !currentBreakpoint.isXS || isLoading;

  const headerLeftXS = useHeaderLeft({ items: headerLeftItems });
  const headerRightXS = useHeaderRight({ close: headerRightClose });

  const headerLeft = shouldHideControls ? renderNoHeaderLeft : headerLeftXS;
  const headerRight = shouldHideControls ? undefined : headerRightXS;
  const { headerLeftOptions, headerRightOptions } = useHeaderButtonOptions({
    headerLeft,
    headerRight,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  /**
   * Conditionally disable hardware back button on Android.
   */
  useEffect(() => {
    return handleHardwareBackButtonPress(() => isLoading);
  }, [isLoading]);

  /**
   * Set header controls.
   */
  useEffect(() => {
    const setOptions = navigation.setOptions;

    setOptions({
      ...headerLeftOptions,
      ...headerRightOptions,

      // NOTE: We use a custom back button, thus we need to hide the default one.
      headerBackVisible: false,
    });
  }, [headerLeftOptions, headerRightOptions, isLoading, navigation]);

  /**
   * Conditionally enable "Swipe to close" functionality.
   */
  useEffect(() => {
    const parentStack = navigation.getParent();

    parentStack?.setOptions({
      gestureEnabled: !isLoading,
    });
  }, [navigation, isLoading]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A no-op headerLeft renderer that prevents the default `HeaderBackButton`
 * from mounting.
 */
function renderNoHeaderLeft() {
  return null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseJoinOrSignScreenHeaderButtonsParams = {
  isLoading: boolean;
};
